import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CommentsService, Comment, CreateCommentData } from '@/services/social/commentsService';
import { toast } from 'sonner';

export const useComments = (interviewId: string) => {
  const queryClient = useQueryClient();

  const {
    data: comments = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['comments', interviewId],
    queryFn: () => CommentsService.getCommentsForInterview(interviewId),
    enabled: !!interviewId,
  });

  const createCommentMutation = useMutation({
    mutationFn: (data: CreateCommentData) => CommentsService.createComment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', interviewId] });
      toast.success('Kommentar lagt til');
    },
    onError: (error) => {
      console.error('Error creating comment:', error);
      toast.error('Kunne ikke legge til kommentar');
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) => 
      CommentsService.updateComment(commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', interviewId] });
      toast.success('Kommentar oppdatert');
    },
    onError: (error) => {
      console.error('Error updating comment:', error);
      toast.error('Kunne ikke oppdatere kommentar');
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => CommentsService.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', interviewId] });
      toast.success('Kommentar slettet');
    },
    onError: (error) => {
      console.error('Error deleting comment:', error);
      toast.error('Kunne ikke slette kommentar');
    },
  });

  const {
    data: commentStats,
    isLoading: isStatsLoading,
  } = useQuery({
    queryKey: ['comment-stats', interviewId],
    queryFn: () => CommentsService.getCommentStats(interviewId),
    enabled: !!interviewId,
  });

  return {
    comments,
    isLoading,
    error,
    commentStats,
    isStatsLoading,
    refetch,
    createComment: createCommentMutation.mutate,
    updateComment: updateCommentMutation.mutate,
    deleteComment: deleteCommentMutation.mutate,
    isCreating: createCommentMutation.isPending,
    isUpdating: updateCommentMutation.isPending,
    isDeleting: deleteCommentMutation.isPending,
  };
};